/**
 * userApi.ts — calls to the Django /newspaper/api/v1/auth/ endpoints.
 * All requests carry the Cognito access token as an Authorization: Bearer header.
 */

const BASE = '/newspaper/api/v1'

export interface UserPreferences {
  username: string
  theme: 'light' | 'dark' | 'paper'
  language: string
  font_size: 'small' | 'medium' | 'large'
  city_slug: string
  news_language: string
  created?: boolean
}

function authHeaders(token: string): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

/**
 * POST /auth/sync/
 * Create-or-get the Django User for the logged-in Cognito user.
 * Returns the stored theme + language preferences.
 */
export async function syncUser(token: string): Promise<UserPreferences> {
  const res = await fetch(`${BASE}/auth/sync/`, {
    method: 'POST',
    headers: authHeaders(token),
  })
  if (res.status === 401) {
    // Token expired or invalid — clear auth state and redirect
    localStorage.removeItem('radiri-auth-token')
    window.location.href = '/login'
    throw new Error('Auth token expired')
  }
  if (!res.ok) throw new Error(`auth/sync failed: ${res.status}`)
  return res.json() as Promise<UserPreferences>
}

/**
 * GET /auth/me/
 * Return the current user's stored preferences.
 */
export async function getMe(token: string): Promise<UserPreferences> {
  const res = await fetch(`${BASE}/auth/me/`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (res.status === 401) {
    // Token expired or invalid — clear auth state and redirect
    localStorage.removeItem('radiri-auth-token')
    window.location.href = '/login'
    throw new Error('Auth token expired')
  }
  if (!res.ok) throw new Error(`auth/me GET failed: ${res.status}`)
  return res.json() as Promise<UserPreferences>
}

/**
 * PATCH /auth/me/
 * Update theme and/or language preference.
 */
export async function updatePreferences(
  token: string,
  prefs: { theme?: string; language?: string; font_size?: string; city_slug?: string; news_language?: string },
): Promise<UserPreferences> {
  const res = await fetch(`${BASE}/auth/me/`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(prefs),
  })
  if (res.status === 401) {
    // Token expired or invalid — clear auth state and redirect
    localStorage.removeItem('radiri-auth-token')
    window.location.href = '/login'
    throw new Error('Auth token expired')
  }
  if (!res.ok) throw new Error(`auth/me PATCH failed: ${res.status}`)
  return res.json() as Promise<UserPreferences>
}
